/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { Button, Input } from 'components/atoms';
import { DateInputPicker } from 'components/atoms/DateInputPicker';
import { Modal } from 'components/molecules/Modal';
import { useGetUserProfile, useUpdateUserProfile } from 'framework/api/methods';
import { FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { setUserDetails } from 'store/reducers/user/userSlice';
import isEmail from 'utils/isEmail';

export interface ProfileProps {
  [x: string]: any;
}

export const Profile: FC<ProfileProps> = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    addressLine1: '-',
    addressLine2: '-',
    addressLine3: '-',
    dateOfBirth: '',
    accHolderName: '-',
    accNo: '-',
    bank: '-',
    branch: '-',
  });

  const [warningModal, setWarningModal] = useState(false);

  const notify = (message: any, type: any) => {
    if (type === 'error') {
      toast.error(message, {
        position: 'bottom-center',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
    if (type === 'success') {
      toast.success(message, {
        position: 'bottom-center',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  };

  const formatDateToYYYYMMDD = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  const convertToLocalNumber = (phoneNumber: string) => {
    const str = String(phoneNumber);
    if (str.startsWith('94')) {
      return `0${str.slice(2)}`;
    }
    return str;
  };

  const onSuccessUserProfile = (data: any) => {
    const userdata = data?.userData;
    if (data?.userStatus !== 'completed') {
      setWarningModal(true);
    } else {
      setWarningModal(false);
    }
    setFormData({
      firstName: userdata.first_name || '',
      lastName: userdata.last_name || '',
      email: userdata.email || '',
      phone: convertToLocalNumber(userdata.phone_number) || '',
      addressLine1: '-',
      addressLine2: '-',
      addressLine3: '-',
      dateOfBirth: userdata.dob || '',
      accHolderName: '-',
      accNo: '-',
      bank: '-',
      branch: '-',
    });

    dispatch(setUserDetails({ name: userdata?.username, status: data?.userStatus }));
  };

  const onErrorUserProfile = ({ response: { data: errData }, response }: any) => {
    if (response?.status === 405 || response?.status === 403 || response?.status === 501) {
      notify('Session expired.', 'error');
      setTimeout(() => {
        navigate('/auth/login');
        localStorage.clear();
      }, 2000);
    } else {
      notify(errData?.message || 'Something went wrong.', 'error');
    }
  };

  /** Get all cards API */
  const { refetch: getUserProfile } = useGetUserProfile(onSuccessUserProfile, onErrorUserProfile);

  useEffect(() => {
    getUserProfile();
  }, []);

  const { mutate: updateUserProfileMutate, isLoading: updateUserProfileIsLoading } =
    useUpdateUserProfile();

  // function to request Otp for registered / unregistered users
  const updateUserProfile = () => {
    updateUserProfileMutate(
      {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        address1: formData.addressLine1,
        address2: formData.addressLine2,
        address3: formData.addressLine3,
        dob: formData.dateOfBirth,
        bankDetails: {
          account_number: formData.accNo,
          account_holder_name: formData.accHolderName,
          bank_name: formData.bank,
          branch_name: formData.branch,
        },
      },
      {
        onSuccess: ({ data: successData }: any) => {
          getUserProfile();
          notify(successData?.message || 'Updated successfully', 'success');
        },
        onError: ({ response: { data: errData }, response }: any) => {
          if (response?.status === 405 || response?.status === 403 || response?.status === 501) {
            notify('Session expired.', 'error');
            setTimeout(() => {
              navigate('/auth/login');
              localStorage.clear();
            }, 2000);
          } else {
            notify(errData?.message || 'Something went wrong.', 'error');
          }
        },
      },
    );
  };

  return (
    <div className="flex flex-col justify-center items-center w-full h-full md:p-8 p-2 overflow-y-auto">
      <div className="bg-[#ffffff] shadow-md px-10 py-4 flex flex-col md:w-2/3 w-full md:h-fit h-full rounded overflow-auto">
        <div className="flex w-full justify-center items-center h-fit text-[#F7941E] pb-5">
          Profile Details
        </div>

        <div className="flex w-full h-full gap-x-5 md:flex-row flex-col">
          <div className="md:w-1/2 flex flex-col w-full">
            <div className="flex gap-2 md:flex-row flex-col">
              <Input
                labelValue="First Name"
                inputClassName="!rounded"
                appearance="gray"
                isRequired
                value={formData.firstName}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    firstName: e.target.value,
                  });
                }}
              />
              <Input
                labelValue="Last Name"
                inputClassName="!rounded"
                appearance="gray"
                isRequired
                value={formData.lastName}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    lastName: e.target.value,
                  });
                }}
              />
            </div>

            <Input
              labelValue="NIC/Passport No"
              inputClassName="!rounded"
              className="mt-2"
              appearance="gray"
              isRequired
              value={formData.dateOfBirth}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  dateOfBirth: e.target.value,
                });
              }}
            />

            {/* <Input
              labelValue="Address Line 1"
              inputClassName="!rounded"
              className="mt-2"
              appearance="gray"
              isRequired
              value={formData.addressLine1}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  addressLine1: e.target.value,
                });
              }}
            /> */}

            {/* <Input
              labelValue="Address Line 3"
              inputClassName="!rounded"
              appearance="gray"
              className="mt-2"
              value={formData.addressLine3}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  addressLine3: e.target.value,
                });
              }}
            /> */}
          </div>

          <div className="md:w-1/2 w-full flex flex-col gap-y-2">
            <Input
              labelValue="Email"
              inputClassName="!rounded"
              appearance="gray"
              isRequired
              value={formData.email}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  email: e.target.value,
                });
              }}
            />

            <Input
              labelValue="Phone"
              inputClassName="!rounded"
              appearance="gray"
              value={formData.phone}
              isDisabled
            />

            {/* <Input
              labelValue="Address Line 2"
              inputClassName="!rounded"
              appearance="gray"
              className="mt-2"
              value={formData.addressLine2}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  addressLine2: e.target.value,
                });
              }}
            /> */}
          </div>
          {/* <div className="md:hidden flex w-full flex-col gap-y-3 pt-4">
            <div className="flex w-full justify-center items-center h-fit text-[#F7941E]">
              Bank Details
            </div>

            <div className="flex gap-3 flex-col">
              <div className="flex gap-3 items-end md:flex-row flex-col">
                <Input
                  labelValue="Acc Holder Name"
                  inputClassName="!rounded"
                  appearance="gray"
                  isRequired
                  value={formData.accHolderName}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      accHolderName: e.target.value,
                    });
                  }}
                />

                <Input
                  labelValue="Acc No"
                  inputClassName="!rounded"
                  appearance="gray"
                  isRequired
                  value={formData.accNo}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      accNo: e.target.value,
                    });
                  }}
                />
              </div>

              <div className="flex gap-3  md:flex-row flex-col">
                <Input
                  labelValue="Bank"
                  inputClassName="!rounded"
                  appearance="gray"
                  isRequired
                  value={formData.bank}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      bank: e.target.value,
                    });
                  }}
                />

                <Input
                  labelValue="Branch"
                  inputClassName="!rounded"
                  appearance="gray"
                  isRequired
                  value={formData.branch}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      branch: e.target.value,
                    });
                  }}
                />
              </div>
            </div>
          </div> */}
          <div className="flex md:hidden">
            <Button
              isRounded
              className="my-5"
              disabled={
                !formData.firstName ||
                !formData.lastName ||
                !formData.email ||
                !formData.dateOfBirth
              }
              isLoading={updateUserProfileIsLoading}
              onClick={() => {
                if (isEmail(formData.email)) {
                  updateUserProfile();
                } else {
                  notify('Invalid email address', 'error');
                }
              }}
            >
              Submit
            </Button>
          </div>
        </div>

        {/* <div className="md:flex w-full flex-col gap-y-3 pt-4 hidden">
          <div className="flex w-full justify-center items-center h-fit text-[#F7941E]">
            Bank Details
          </div>

          <div className="flex gap-3 flex-col">
            <div className="flex gap-3 items-end md:flex-row flex-col">
              <Input
                labelValue="Acc Holder Name"
                inputClassName="!rounded"
                appearance="gray"
                isRequired
                value={formData.accHolderName}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    accHolderName: e.target.value,
                  });
                }}
              />

              <Input
                labelValue="Acc No"
                inputClassName="!rounded"
                appearance="gray"
                isRequired
                value={formData.accNo}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    accNo: e.target.value,
                  });
                }}
              />
            </div>

            <div className="flex gap-3  md:flex-row flex-col">
              <Input
                labelValue="Bank"
                inputClassName="!rounded"
                appearance="gray"
                isRequired
                value={formData.bank}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    bank: e.target.value,
                  });
                }}
              />

              <Input
                labelValue="Branch"
                inputClassName="!rounded"
                appearance="gray"
                isRequired
                value={formData.branch}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    branch: e.target.value,
                  });
                }}
              />
            </div>
          </div>
        </div> */}

        <div className="md:flex hidden">
          <Button
            isRounded
            className="mt-5"
            disabled={
              !formData.firstName || !formData.lastName || !formData.email || !formData.dateOfBirth
            }
            isLoading={updateUserProfileIsLoading}
            onClick={() => {
              if (isEmail(formData.email)) {
                updateUserProfile();
              } else {
                notify('Invalid email address', 'error');
              }
            }}
          >
            Submit
          </Button>
        </div>
      </div>

      <Modal
        isActive={warningModal}
        isHeaderShow={false}
        isFooterShow={false}
        onOverlayClick={() => {
          setWarningModal(false);
        }}
      >
        <div className="flex flex-col w-full">
          <div className="w-full flex justify-end items-end px-2 pt-1">
            <i
              className="ri-close-line text-base text-[#F7941E]"
              onClick={() => {
                setWarningModal(false);
              }}
            />
          </div>
          <div className="flex flex-col justify-center items-center">
            <i className="ri-question-line text-[35px] text-[#F7941E]" />
            <div className="pt-1 text-base text-center text-[#737791] pb-6 px-2">
              Your profile is incomplete. Please complete your profile before uploading videos.
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Profile;
